import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import {
  CdMannerTitleHeldAs,
  MANNER_TITLE_HELD_AS_OPTIONS,
  PropertyInformationSection
} from 'src/app/cd-request/models/closing-disclosure-doc-request.model';
import { EnumerationService } from 'src/app/services/enumeration-service';
import { EnumerationItem } from 'src/app/models/simple-enum-item.model';
import { splitCamelCaseAndCapitals } from 'src/app/core/services/string-utils';

export interface PropertyInformationFormData {
  propertyAddress: string;
  city: string;
  state: string;
  zip: string;
  mersMin: string;
  propertyType: string;
  mannerTitleHeldAs: CdMannerTitleHeldAs | null;
  vesting: string;
  willPoaBeUsed: boolean | null;
  willLoanCloseInTrust: boolean | null;
  nonBorrowerTitleHolder: boolean | null;
  nonBorrowerName: string;
  nonBorrowerPhone: string;
  nonBorrowerEmail: string;
  warehouse: string;
  isLlc: boolean | null;
}

@Component({
  selector: 'icd-property-information',
  templateUrl: './icd-property-information.component.html',
  styleUrls: ['./icd-property-information.component.scss'],
  standalone: false
})
export class IcdPropertyInformationComponent extends ApplicationContextBoundComponent implements OnInit, OnChanges {

  @ViewChild('propertyWarehouseForm') propertyInformationForm: NgForm;

  @Input() propertyInformation: PropertyInformationSection | undefined;
  /** When true, hides MERS MIN and Warehouse fields (ICD flow) */
  @Input() isIcdFlow: boolean = false;

  formData: PropertyInformationFormData = {
    propertyAddress: '',
    city: '',
    state: '',
    zip: '',
    mersMin: '',
    propertyType: '',
    mannerTitleHeldAs: null,
    vesting: '',
    willPoaBeUsed: null,
    willLoanCloseInTrust: null,
    nonBorrowerTitleHolder: null,
    nonBorrowerName: '',
    nonBorrowerPhone: '',
    nonBorrowerEmail: '',
    warehouse: '',
    isLlc: null
  };

  yesNoOptions: { value: boolean | null; label: string }[] = [
    { value: null, label: 'Select...' },
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
  ];

  mannerTitleHeldAsOptions = MANNER_TITLE_HELD_AS_OPTIONS;
  states: EnumerationItem[] = [];

  get showNonBorrowerFields(): boolean {
    return this.formData.nonBorrowerTitleHolder === true;
  }

  get propertyTypeDisplay(): string {
    if (!this.formData.propertyType) {
      return '';
    }
    return splitCamelCaseAndCapitals(this.formData.propertyType);
  }

  constructor(
    protected readonly injector: Injector,
    private readonly _enumService: EnumerationService
  ) {
    super(injector);
    this.states = this._enumService.states;
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyInformation']) {
      this.populateFormFromApiData();
    }
  }

  validateForm(): boolean {
    if (this.propertyInformationForm) {
      this.propertyInformationForm.form.markAllAsTouched();

      if (!this.propertyInformationForm.form.valid) {
        return false;
      }

      if (this.showNonBorrowerFields) {
        if (!this.formData.nonBorrowerName || !this.formData.nonBorrowerPhone || !this.formData.nonBorrowerEmail) {
          return false;
        }

        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailPattern.test(this.formData.nonBorrowerEmail)) {
          return false;
        }
      }

      return true;
    }
    return true;
  }

  getFormData(): PropertyInformationFormData {
    return { ...this.formData };
  }

  private populateFormFromApiData(): void {
    if (this.propertyInformation) {
      this.formData.propertyAddress = this.propertyInformation.streetAddress || '';
      this.formData.city = this.propertyInformation.city || '';
      this.formData.state = this.propertyInformation.state || '';
      this.formData.zip = this.propertyInformation.zip || '';
      this.formData.propertyType = this.propertyInformation.propertyType || '';
      this.formData.mersMin = this.propertyInformation.mersMin || '';
      this.formData.mannerTitleHeldAs = this.propertyInformation.mannerTitleHeldAs ?? null;
      this.formData.vesting = this.propertyInformation.vesting || '';
      this.formData.willPoaBeUsed = this.propertyInformation.willUsePoa ?? null;
      this.formData.willLoanCloseInTrust = this.propertyInformation.willCloseInTrust ?? null;
      this.formData.nonBorrowerTitleHolder = this.propertyInformation.nonBorrowerTitleHolder ?? null;
      this.formData.nonBorrowerName = this.propertyInformation.nonBorrowerTitleHolderName || '';
      this.formData.nonBorrowerPhone = this.propertyInformation.nonBorrowerTitleHolderPhone || '';
      this.formData.nonBorrowerEmail = this.propertyInformation.nonBorrowerTitleHolderEmail || '';
      this.formData.warehouse = this.propertyInformation.warehouse || '';
      this.formData.isLlc = this.propertyInformation.isLlc ?? null;
    }
  }
}
